/**
 * Import utilities for converting external data formats to Items.
 * Supports CSV/Excel → Items mapping.
 */

import type { Item } from "../harmonizer/types";

/**
 * Raw row data from CSV/Excel import.
 */
export type RawRow = Record<string, string | number | boolean | null>;

/**
 * Maps raw rows to Items.
 * 
 * Rules:
 * - If idColumn is null, auto-generates IDs (e.g., item-0001, item-0002, ...)
 * - labelColumn is required
 * - All attributeColumns become attributes[key] on each Item
 * 
 * @param rows - Raw rows from CSV/Excel
 * @param idColumn - Column name for item ID (null to auto-generate)
 * @param labelColumn - Column name for item label (required)
 * @param attributeColumns - Column names to include as attributes
 * @returns Array of Items
 */
export function mapRowsToItems(
  rows: RawRow[],
  idColumn: string | null,
  labelColumn: string,
  attributeColumns: string[]
): Item[] {
  const items: Item[] = [];
  let autoIdCounter = 1;

  for (const row of rows) {
    // Get or generate ID
    let id: string;
    if (idColumn !== null && idColumn in row) {
      const idValue = row[idColumn];
      if (typeof idValue === "string") {
        id = idValue;
      } else if (idValue !== null && idValue !== undefined) {
        id = String(idValue);
      } else {
        // Fall back to auto-generation if ID column is null/undefined
        id = `item-${String(autoIdCounter).padStart(4, "0")}`;
        autoIdCounter++;
      }
    } else {
      // Auto-generate ID
      id = `item-${String(autoIdCounter).padStart(4, "0")}`;
      autoIdCounter++;
    }

    // Get label (required)
    if (!(labelColumn in row)) {
      throw new Error(
        `Row missing required label column '${labelColumn}'. Row: ${JSON.stringify(row)}`
      );
    }

    const labelValue = row[labelColumn];
    if (labelValue === null || labelValue === undefined) {
      throw new Error(
        `Row has null/undefined label in column '${labelColumn}'. Row: ${JSON.stringify(row)}`
      );
    }

    const label = typeof labelValue === "string" ? labelValue : String(labelValue);

    // Get description (optional, from description column if present)
    let description: string | undefined;
    if ("description" in row && row.description !== null && row.description !== undefined) {
      description = typeof row.description === "string" ? String(row.description) : String(row.description);
    }

    // Build attributes from attributeColumns
    const attributes: Record<string, string | number | boolean | null> = {};
    for (const attrCol of attributeColumns) {
      if (attrCol in row) {
        const value = row[attrCol];
        // Preserve type: string, number, boolean, or null
        if (value === null || value === undefined) {
          attributes[attrCol] = null;
        } else if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
          attributes[attrCol] = value;
        } else {
          // Convert to string as fallback
          attributes[attrCol] = String(value);
        }
      }
    }

    items.push({
      id,
      label,
      description,
      attributes,
    });
  }

  return items;
}

/**
 * Helper function to parse CSV text into RawRow array.
 * 
 * Note: This is a basic implementation. For production use, consider using
 * a dedicated CSV parsing library like 'csv-parse' or 'papaparse'.
 * 
 * @param csvText - CSV text content
 * @param hasHeader - Whether the first row is a header (default: true)
 * @returns Array of raw rows
 */
export function parseCSV(csvText: string, hasHeader: boolean = true): RawRow[] {
  const lines = csvText.split("\n").map((line) => line.trim()).filter((line) => line.length > 0);
  
  if (lines.length === 0) {
    return [];
  }

  // Parse header
  const headerLine = lines[0];
  const headers = parseCSVLine(headerLine);
  
  // Parse data rows
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const rows: RawRow[] = [];

  for (const line of dataLines) {
    const values = parseCSVLine(line);
    const row: RawRow = {};
    
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i];
      const value = values[i] || "";
      
      // Try to parse as number or boolean
      let parsedValue: string | number | boolean | null = value;
      
      if (value === "" || value === "null" || value === "NULL") {
        parsedValue = null;
      } else if (value === "true" || value === "TRUE") {
        parsedValue = true;
      } else if (value === "false" || value === "FALSE") {
        parsedValue = false;
      } else {
        const numValue = Number(value);
        if (!isNaN(numValue) && value.trim() !== "") {
          parsedValue = numValue;
        } else {
          parsedValue = value;
        }
      }
      
      row[header] = parsedValue;
    }
    
    rows.push(row);
  }

  return rows;
}

/**
 * Parses a single CSV line, handling quoted fields.
 */
function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      // End of field
      values.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  
  // Add last field
  values.push(current);

  return values;
}


